import { auth } from './firebase';
import { getTenantHeaders } from './tenant-context';
import { getApiDebugInfo, getApiUrl } from './api-config';

//console.log('🔍 API Configuration:', getApiDebugInfo());

//console.log('Tenant headers:', getTenantHeaders());

// Helper function to get auth token
export const getAuthToken = async (): Promise<string | null> => {
  const user = auth.currentUser;
  if (!user) return null;
  return await user.getIdToken();
};

// Base API call function
export const apiCall = async (endpoint: string, options: RequestInit = {}): Promise<any> => {
  const token = await getAuthToken();
  const tenantHeaders = getTenantHeaders();
  
  const fullUrl = getApiUrl(endpoint);
  
  /*console.log('🔍 API Call Debug:', {
    endpoint: fullUrl,
    tenantHeaders,
    hasToken: !!token,
    isDev: import.meta.env.DEV
  });*/
  
  const response = await fetch(fullUrl, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` }),
      ...tenantHeaders,
      ...options.headers,
    },
  });
  
  /*console.log('🔍 API Response:', {
    status: response.status,
    statusText: response.statusText,
    headers: Object.fromEntries(response.headers.entries())
  });*/
  
  if (!response.ok) {
    const responseText = await response.text();
    console.log('🔍 API Error Response:', responseText);
    
    try {
      const error = JSON.parse(responseText);
      throw new Error(error.error || `API request failed: ${response.status}`);
    } catch (parseError) {
      throw new Error(`API request failed: ${response.status} - ${responseText.substring(0, 100)}`);
    }
  }
  
  return response.json();
};

// Auth API functions
export const authAPI = {
  getProfile: () => apiCall('/auth/me'),
  updateProfile: (updates: any) => apiCall('/auth/me', {
    method: 'PUT',
    body: JSON.stringify(updates),
  }),
  getPermissions: () => apiCall('/auth/permissions'),
  getRoles: () => apiCall('/auth/roles'),
  createProfile: (profileData: any) => apiCall('/auth/create-profile', {
    method: 'POST',
    body: JSON.stringify(profileData),
  }),
};

// Tenant API functions
export const tenantAPI = {
  getBySlug: (slug: string) => apiCall(`/tenants/${slug}`),
  getSettings: () => apiCall('/settings'),
  updateSettings: (settings: any) => apiCall('/settings', {
    method: 'PUT',
    body: JSON.stringify(settings),
  }),
};

// User management API functions
export const usersAPI = {
  list: () => apiCall('/users'),
  getById: (id: string) => apiCall(`/users/${id}`),
  create: (userData: any) => apiCall('/users', {
    method: 'POST',
    body: JSON.stringify(userData),
  }),
  update: (id: string, updates: any) => apiCall(`/users/${id}`, {
    method: 'PUT',
    body: JSON.stringify(updates),
  }),
  delete: (id: string) => apiCall(`/users/${id}`, { method: 'DELETE' }),
  assignRole: (userId: string, roleId: string) => apiCall(`/users/${userId}/roles`, {
    method: 'POST',
    body: JSON.stringify({ roleId }),
  }),
  removeRole: (userId: string, roleId: string) => apiCall(`/users/${userId}/roles/${roleId}`, {
    method: 'DELETE',
  }),
};

// Role management API functions
export const rolesAPI = {
  list: () => apiCall('/roles'),
  getById: (id: string) => apiCall(`/roles/${id}`),
  create: (roleData: any) => apiCall('/roles', {
    method: 'POST',
    body: JSON.stringify(roleData),
  }),
  update: (id: string, updates: any) => apiCall(`/roles/${id}`, {
    method: 'PUT',
    body: JSON.stringify(updates),
  }),
  delete: (id: string) => apiCall(`/roles/${id}`, { method: 'DELETE' }),
  getPermissions: (id: string) => apiCall(`/roles/${id}/permissions`),
  updatePermissions: (id: string, permissions: string[]) => apiCall(`/roles/${id}/permissions`, {
    method: 'PUT',
    body: JSON.stringify({ permissions }),
  }),
};

// Business data API functions
export const customersAPI = {
  list: () => apiCall('/customers'),
  getById: (id: string) => apiCall(`/customers/${id}`),
  create: (customerData: any) => apiCall('/customers', {
    method: 'POST',
    body: JSON.stringify(customerData),
  }),
  update: (id: string, updates: any) => apiCall(`/customers/${id}`, {
    method: 'PUT',
    body: JSON.stringify(updates),
  }),
  delete: (id: string) => apiCall(`/customers/${id}`, { method: 'DELETE' }),
};

export const vehiclesAPI = {
  list: () => apiCall('/vehicles'),
  getById: (id: string) => apiCall(`/vehicles/${id}`),
  create: (vehicleData: any) => apiCall('/vehicles', {
    method: 'POST',
    body: JSON.stringify(vehicleData),
  }),
  update: (id: string, updates: any) => apiCall(`/vehicles/${id}`, {
    method: 'PUT',
    body: JSON.stringify(updates),
  }),
  delete: (id: string) => apiCall(`/vehicles/${id}`, { method: 'DELETE' }),
};

export const serviceRequestsAPI = {
  list: () => apiCall('/service-requests'),
  getById: (id: string) => apiCall(`/service-requests/${id}`),
  create: (requestData: any) => apiCall('/service-requests', {
    method: 'POST',
    body: JSON.stringify(requestData),
  }),
  update: (id: string, updates: any) => apiCall(`/service-requests/${id}`, {
    method: 'PUT',
    body: JSON.stringify(updates),
  }),
  delete: (id: string) => apiCall(`/service-requests/${id}`, { method: 'DELETE' }),
};

export const invoicesAPI = {
  list: () => apiCall('/invoices'),
  getById: (id: string) => apiCall(`/invoices/${id}`),
  create: (invoiceData: any) => apiCall('/invoices', {
    method: 'POST',
    body: JSON.stringify(invoiceData),
  }),
  update: (id: string, updates: any) => apiCall(`/invoices/${id}`, {
    method: 'PUT',
    body: JSON.stringify(updates),
  }),
  delete: (id: string) => apiCall(`/invoices/${id}`, { method: 'DELETE' }),
};

export const inventoryAPI = {
  list: () => apiCall('/inventory'),
  getById: (id: string) => apiCall(`/inventory/${id}`),
  create: (itemData: any) => apiCall('/inventory', {
    method: 'POST',
    body: JSON.stringify(itemData),
  }),
  update: (id: string, updates: any) => apiCall(`/inventory/${id}`, {
    method: 'PUT',
    body: JSON.stringify(updates),
  }),
  delete: (id: string) => apiCall(`/inventory/${id}`, { method: 'DELETE' }),
};

export const timeAPI = {
  list: () => apiCall('/time'),
  getById: (id: string) => apiCall(`/time/${id}`),
  create: (entryData: any) => apiCall('/time', {
    method: 'POST',
    body: JSON.stringify(entryData),
  }),
  update: (id: string, updates: any) => apiCall(`/time/${id}`, {
    method: 'PUT',
    body: JSON.stringify(updates),
  }),
  delete: (id: string) => apiCall(`/time/${id}`, { method: 'DELETE' }),
};

// Audit Logs API
export const auditLogsAPI = {
  list: (params?: Record<string, string>) => {
    const qs = new URLSearchParams(params || {}).toString();
    return apiCall(`/audit-logs${qs ? `?${qs}` : ''}`);
  },
};